import { HOMEOWNERS_ROUTES } from '@/shared/constants/pages-routes'

export type HomeownerDetailTab = 'home' | 'homeowner' | 'photos' | 'workflow'

type BuildHomeownersDetailUrlOptions = {
  /** Current page URL to store as the return URL. Defaults to window.location. */
  currentUrl?: string
  /** Detail tab to open. Defaults to 'home'. */
  tab?: HomeownerDetailTab
}

/**
 * Builds a URL to the homeowners detail page with a 'from' parameter
 * that stores the current page URL for navigation back.
 * Always includes a tab query param (default: home) so the correct tab
 * opens even when home data is still loading on first visit.
 *
 * @param homeownerId - The homeowner ID
 * @param currentUrlOrOptions - Legacy: current URL string; or options object
 * @returns The URL with tab and optional from parameters
 */
export function buildHomeownersDetailUrlWithReturn(
  homeownerId: string | number,
  currentUrlOrOptions?: string | BuildHomeownersDetailUrlOptions
): string {
  const options: BuildHomeownersDetailUrlOptions =
    typeof currentUrlOrOptions === 'string'
      ? { currentUrl: currentUrlOrOptions }
      : (currentUrlOrOptions ?? {})

  const tab = options.tab ?? 'home'
  const baseUrl = HOMEOWNERS_ROUTES.details(homeownerId)
  const params = new URLSearchParams()
  params.set('tab', tab)

  let currentUrl = options.currentUrl
  // Get current URL if not provided
  if (!currentUrl && typeof window !== 'undefined') {
    currentUrl = window.location.pathname + window.location.search
  }

  // Only add 'from' if we have a current URL and we're not already on a homeowners detail page
  // (to avoid storing tab navigation as the referrer)
  if (currentUrl && !currentUrl.includes(`/homeowners/${homeownerId}`)) {
    params.set('from', currentUrl)
  }

  return `${baseUrl}?${params.toString()}`
}

/**
 * Gets the return URL from sessionStorage or query parameter
 * Falls back to homeowners base route if neither is available
 * 
 * @param homeownerId - The current homeowner ID (to check if we're on the detail page)
 * @returns The URL to return to, or null if not found
 */
export function getReturnUrl(homeownerId?: string | number): string | null {
  if (typeof window === 'undefined') return null
  
  // First check sessionStorage
  const storageKey = `homeowners_return_${homeownerId || 'default'}`
  const storedReturn = sessionStorage.getItem(storageKey)
  if (storedReturn) {
    return storedReturn
  }
  
  // Fallback to query parameter
  const urlParams = new URLSearchParams(window.location.search)
  const fromParam = urlParams.get('from')
  if (fromParam) {
    // URLSearchParams.get() returns already-decoded values. decodeURIComponent would throw URIError
    // when the value contains literal % (e.g. workscopeStatus=25%,50%,75%).
    return fromParam
  }
  
  return null
}

/**
 * Stores the return URL in sessionStorage
 * Only stores if we're navigating TO the detail page (not changing tabs within it)
 * 
 * @param returnUrl - The URL to return to
 * @param homeownerId - The homeowner ID
 */
export function storeReturnUrl(returnUrl: string, homeownerId?: string | number): void {
  if (typeof window === 'undefined') return
  
  // Only store if the return URL is not the same base path as current page
  // (to avoid storing tab navigation as the referrer)
  const currentPath = window.location.pathname
  const returnPath = new URL(returnUrl, window.location.origin).pathname
  
  // Check if we're navigating to a different base path
  // (e.g., from /homeowners to /homeowners/123, not from /homeowners/123?tab=home to /homeowners/123?tab=photos)
  const isDifferentBasePath = !currentPath.includes(returnPath) && !returnPath.includes(currentPath)
  
  if (isDifferentBasePath || returnPath !== currentPath) {
    const storageKey = `homeowners_return_${homeownerId || 'default'}`
    sessionStorage.setItem(storageKey, returnUrl)
  }
}
